import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { useTheme } from '../../../theming/useTheme';
import { Image } from '../../partials/image/Image';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { AdditionalStyleSheet } from '../../components.utils';
import { Thumbnail } from '../../partials/image/image.types';
import { Link } from '../../partials/link/Link';
import { H1, H4, P, Span, semanticCSS } from '../../../typography/semanticTags';
import { BaseUnit } from '../../../theming/baseUnitDefinition';
import { createImageWidths } from '../../partials/image/image.utils';

const componentConfigThemeKey = 'errorPage';

interface ErrorComponentProps {
  image: Thumbnail;
  title: string;
  secondaryTitle: string;
  content: string;
  button: {
    text: string;
    href: string;
  };
}

interface ErrorComponentThemeProps {
  baseUnit: BaseUnit;
  titleColor: string;
  textColor: string;
  buttonColor: string;
  buttonBackgroundColor: string;
}

const createErrorStyle = (theme: ErrorComponentThemeProps) => {
  const { baseUnit, titleColor, textColor, buttonColor, buttonBackgroundColor } = theme;
  return StyleSheet.create({
    title: {
      textAlign: 'center',
      color: titleColor,
    },
    secondaryTitle: {
      textAlign: 'center',
      color: titleColor,
      [MEDIA_BREAKPOINTS.large]: {
        marginTop: 0,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        marginTop: `${baseUnit / 2}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginTop: `${baseUnit / 2}px`,
      },
    },
    content: {
      textAlign: 'center',
      color: textColor,
    },
    buttonWrapper: {
      textAlign: 'center',
      margin: `${baseUnit}px 0`,
    },
    button: {
      color: buttonColor,
      backgroundColor: buttonBackgroundColor,
      borderRadius: '16px',
      textDecoration: 'none',
      padding: `${baseUnit / 4}px ${baseUnit / 2}px`,
    },
  });
};

const createImageErrorStyle = (theme: ErrorComponentThemeProps) => {
  const { baseUnit } = theme;
  return StyleSheet.create({
    style: {
      margin: `${baseUnit * 2}px auto ${baseUnit}px auto`,
      [MEDIA_BREAKPOINTS.large]: {
        width: '300px',
        height: '300px',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: '180px',
        height: '180px',
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '180px',
        height: '180px',
      },
    },
  });
};

const imageWidths = createImageWidths(300, 300, 300);

export const Error: React.FunctionComponent<ErrorComponentProps> = props => {
  const { title, image, secondaryTitle, content, button } = props;
  const themeProps = useTheme<ErrorComponentThemeProps>(componentConfigThemeKey);
  const aspectRatio = { x: 1, y: 1 };
  const errorStyle = createErrorStyle(themeProps);
  const imageErrorStyle = createImageErrorStyle(themeProps) as AdditionalStyleSheet;

  return (
    <ErrorBoundary>
      <div className={css(imageErrorStyle.style)}>
        <Image {...image} additionalStyle={imageErrorStyle} aspectRatio={aspectRatio} imageWidths={imageWidths} />
      </div>
      <H1 styles={semanticCSS(errorStyle.title)}>{title}</H1>
      <H4 styles={semanticCSS(errorStyle.secondaryTitle)}>{secondaryTitle}</H4>
      <P styles={semanticCSS(errorStyle.content)}>{content}</P>
      <div className={css(errorStyle.buttonWrapper)}>
        <Link href={button.href} className={css(errorStyle.button)}><Span>{button.text}</Span></Link>
      </div>
    </ErrorBoundary>
  );
};
